import { normalizeImagePaint, type ImageFillMode, type ImagePaint } from "@/document/model/styles";
import { imageRectForPaint, rectToNormalizedCrop } from "@/document/operations/imagePaintGeometry";
import type { Rect } from "@/lib/math/rect";

export type ImageFillHandle = "body" | "topLeft" | "topRight" | "bottomRight" | "bottomLeft";

export interface ImageFillGeometry {
  imageRect: Rect;
  handles: Array<{ handle: Exclude<ImageFillHandle, "body">; x: number; y: number }>;
}

export function imageFillGeometry(bounds: Rect, paint: ImagePaint): ImageFillGeometry {
  const imageRect = imageRectForPaint(bounds, paint);
  return {
    imageRect,
    handles: [
      { handle: "topLeft", x: imageRect.x, y: imageRect.y },
      { handle: "topRight", x: imageRect.x + imageRect.width, y: imageRect.y },
      { handle: "bottomRight", x: imageRect.x + imageRect.width, y: imageRect.y + imageRect.height },
      { handle: "bottomLeft", x: imageRect.x, y: imageRect.y + imageRect.height },
    ],
  };
}

export function imageDrawRectForPaint(bounds: Rect, paint: ImagePaint): Rect {
  const rect = imageRectForPaint(bounds, paint);
  const normalized = normalizeImagePaint(paint);
  let { x, y, width, height } = rect;
  if (normalized.flipX) {
    x += width;
    width = -width;
  }
  if (normalized.flipY) {
    y += height;
    height = -height;
  }
  return { x, y, width, height };
}

export function hitTestImageFillGizmo(
  bounds: Rect,
  paint: ImagePaint,
  point: { x: number; y: number },
  zoom: number,
): ImageFillHandle | undefined {
  const geometry = imageFillGeometry(bounds, paint);
  const radius = 9 / zoom;
  for (const handle of [...geometry.handles].reverse()) {
    if (Math.hypot(point.x - handle.x, point.y - handle.y) <= radius) {
      return handle.handle;
    }
  }

  if (point.x >= geometry.imageRect.x && point.x <= geometry.imageRect.x + geometry.imageRect.width && point.y >= geometry.imageRect.y && point.y <= geometry.imageRect.y + geometry.imageRect.height) {
    return "body";
  }

  return undefined;
}

export function imagePaintWithMode(paint: ImagePaint, bounds: Rect, fillMode: ImageFillMode): ImagePaint {
  const normalized = normalizeImagePaint(paint);
  const nextRect = imageRectForPaint(bounds, { ...normalized, fillMode });
  return normalizeImagePaint({
    ...normalized,
    fillMode,
    crop: rectToNormalizedCrop(bounds, nextRect),
  });
}

export function imagePaintWithMovedCrop(paint: ImagePaint, bounds: Rect, dx: number, dy: number): ImagePaint {
  const startRect = imageRectForPaint(bounds, paint);
  return normalizeImagePaint({
    ...paint,
    fillMode: "crop",
    crop: rectToNormalizedCrop(bounds, { ...startRect, x: startRect.x + dx, y: startRect.y + dy }),
  });
}

export function imagePaintWithResizedCrop(
  paint: ImagePaint,
  bounds: Rect,
  handle: Exclude<ImageFillHandle, "body">,
  dx: number,
  dy: number,
): ImagePaint {
  const startRect = imageRectForPaint(bounds, paint);
  const aspectRatio = Math.max(0.0001, paint.intrinsicWidth / paint.intrinsicHeight);
  const opposite = oppositePoint(startRect, handle);
  const dragged = draggedPoint(startRect, handle, dx, dy);
  const candidateWidth = Math.max(1, Math.abs(dragged.x - opposite.x));
  const candidateHeight = Math.max(1, Math.abs(dragged.y - opposite.y));
  const widthFromHeight = candidateHeight * aspectRatio;
  const heightFromWidth = candidateWidth / aspectRatio;
  const useWidth = Math.abs(candidateWidth - startRect.width) >= Math.abs(candidateHeight - startRect.height);
  const width = useWidth ? candidateWidth : widthFromHeight;
  const height = useWidth ? heightFromWidth : candidateHeight;
  const x = handle === "topLeft" || handle === "bottomLeft" ? opposite.x - width : opposite.x;
  const y = handle === "topLeft" || handle === "topRight" ? opposite.y - height : opposite.y;

  return normalizeImagePaint({
    ...paint,
    fillMode: "crop",
    crop: rectToNormalizedCrop(bounds, { x, y, width, height }),
  });
}

function oppositePoint(rect: Rect, handle: Exclude<ImageFillHandle, "body">): { x: number; y: number } {
  switch (handle) {
    case "topLeft":
      return { x: rect.x + rect.width, y: rect.y + rect.height };
    case "topRight":
      return { x: rect.x, y: rect.y + rect.height };
    case "bottomRight":
      return { x: rect.x, y: rect.y };
    case "bottomLeft":
      return { x: rect.x + rect.width, y: rect.y };
  }
}

function draggedPoint(rect: Rect, handle: Exclude<ImageFillHandle, "body">, dx: number, dy: number): { x: number; y: number } {
  switch (handle) {
    case "topLeft":
      return { x: rect.x + dx, y: rect.y + dy };
    case "topRight":
      return { x: rect.x + rect.width + dx, y: rect.y + dy };
    case "bottomRight":
      return { x: rect.x + rect.width + dx, y: rect.y + rect.height + dy };
    case "bottomLeft":
      return { x: rect.x + dx, y: rect.y + rect.height + dy };
  }
}
